export enum ApplicationStatus {
  ASSIGNED_FOR_REVIEW = "ASSIGNED_FOR_REVIEW",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export interface ApplicationFormValues {
  // Application core fields
  institutionId: string;
  courseId: string;
  email: string;

  // Personal Information - BasicDetails
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  isSingleNameOnly: boolean;
  dateOfBirth: Date | null;
  gender: string;
  customGender: string;
  countryOfBirth: string;
  countryOfCitizenship: string;
  isCitizenshipSameAsBirth: boolean;
  maritalStatus: string;
  registeredDateOfMarriage: Date | null;

  // Personal Information - ContactDetails
  phone: {
    countryCode: string;
    areaCode: string | null;
    number: string;
    extension: string | null;
  };
  mobile: {
    countryCode: string;
    areaCode: string | null;
    number: string;
    extension: string | null;
  };
  currentAddressStreet1: string;
  currentAddressStreet2: string;
  currentAddressCity: string;
  currentAddressState: string;
  currentAddressPostalCode: string;
  currentAddressCountry: string;
  homeAddressStreet1: string;
  homeAddressStreet2: string;
  homeAddressCity: string;
  homeAddressState: string;
  homeAddressPostalCode: string;
  homeAddressCountry: string;

  isHomeSameAsCurrent: boolean;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: {
    countryCode: string;
    areaCode: string | null;
    number: string;
    extension: string | null;
  };
  emergencyContactMobile: {
    countryCode: string;
    areaCode: string | null;
    number: string;
    extension: string | null;
  };

  // Personal Information - PassportDetails
  passportNumber: string;
  passportExpiryDate: Date | null;
  passportIssuingCountry: string;
  isPassportSameAsBirth: boolean;

  // Personal Information - VisaDetails
  isCurrentlyInCountry: boolean | null;
  yearOfFirstArrival: Date | null;
  hasCurrentVisa: boolean | null;
  currentVisaType: string;
  visaExpiryDate: Date | null;
  isApplyStudentVisa: boolean | null;
  applicationType: string;
  applicationCountry: string;
  hasEntryRefusal: boolean | null;
  entryRefusalDetails: string;

  // English Language Test Scores
  englishTest: string;
  overallScore: number | null;
  reading: number | null;
  writing: number | null;
  speaking: number | null;
  listening: number | null;
  useOfEnglish: number | null;
  dateOfCompletion: Date | null;
  testReportFormNumber: string;
  isEnglishFirstLanguage: boolean | null;
  firstLanguage: string;
  hasEnglishInstructionStudies: boolean | null;
  englishInstructionStudiesCompleted: string;
  englishInstructionStudiesDuration: string;
  hasELICOS: boolean | null;
  elicosInstitution: string;
  elicosStartDate: Date | null;
  elicosEndDate: Date | null;
  elicosWeeks: number | null;

  // Education Details
  completedYear12InAustralia: boolean | null;
  atarScore: number | null;
  highestEducationLevel: string;
  hasBeenExcluded: boolean | null;
  exclusionDetails: string;
  usiNumber: string;

  // Credit Transfer
  isApplyingForCreditTransfer: boolean | null;
  creditTransfer: CreditTransferEntry[];

  // High School
  highSchoolName: string;
  highSchoolCity: string;
  highSchoolCountry: string;
  highSchoolGradeObtained: string;
  highSchoolIsStillAttending: boolean;
  highSchoolStartDate: Date | null;
  highSchoolEndDate: Date | null;

  // Tertiary Education (Multiple entries)
  tertiary1School: string;
  tertiary1Course: string;
  tertiary1City: string;
  tertiary1Country: string;
  tertiary1GradeObtained: string;
  tertiary1IsStillAttending: false;
  tertiary1StartDate: Date | null;
  tertiary1EndDate: Date | null;
  tertiary2School: string;
  tertiary2Course: string;
  tertiary2City: string;
  tertiary2Country: string;
  tertiary2GradeObtained: string;
  tertiary2IsStillAttending: false;
  tertiary2StartDate: Date | null;
  tertiary2EndDate: Date | null;
  tertiary3School: string;
  tertiary3Course: string;
  tertiary3City: string;
  tertiary3Country: string;
  tertiary3GradeObtained: string;
  tertiary3IsStillAttending: false;
  tertiary3StartDate: Date | null;
  tertiary3EndDate: Date | null;

  // Scholarship Financial Support
  hasScholarshipSponsorship: boolean | null;
  scholarshipProviderName: string;
  allowSponsorProgressSharing: boolean | null;
  payingMoreThan50Percent: boolean | null;

  // Health Insurance
  hasCurrentOSHC: boolean | null;
  oshcProviderName: string;
  oshcMembershipNumber: string;
  oshcExpiryDate: Date | null;
  wantUniversityToArrangeOSHC: boolean | null;
  oshcCoverageType: string;

  // Student Support Services
  hasDisabilityMedicalCondition: boolean | null;
  disabilityType: string[];
  otherDetails: string;
  disabilityStartDate: Date | null;

  // Work Experience (Multiple entries)
  employment1CompanyName: string;
  employment1PositionAndDuties: string;
  employment1DateCommenced: Date | null;
  employment1DateEnded: Date | null;
  employment1IsStillWorking: false;
  employment2CompanyName: string;
  employment2PositionAndDuties: string;
  employment2DateCommenced: Date | null;
  employment2DateEnded: Date | null;
  employment2IsStillWorking: false;
  employment3CompanyName: string;
  employment3PositionAndDuties: string;
  employment3DateCommenced: Date | null;
  employment3DateEnded: Date | null;
  employment3IsStillWorking: false;
  currentEmploymentStatus: string;
  reasonForUndertakingCourse: string[];
  reasonOtherDetails: string;

  checklist: {
    completedItems: Array<{
      id: string;
      label: string;
      isCompleted: boolean;
    }>;
    requiredItems: Array<{
      id: string;
      label: string;
      isCompleted: boolean;
    }>;
    isComplete: boolean;
    completionPercentage: number;
  };

  declarations: {
    mandatory: Array<{
      id: string;
      text: string;
      isAccepted: boolean;
    }>;
    marketing: Array<{
      id: string;
      text: string;
      isAccepted: boolean;
    }>;
  };
}

export interface CreditTransferEntry {
  institutionName: string;
  courseCode: string;
  courseName: string;
  dateCompleted: Date | null;
  // equivalentCode: string;
  // equivalentName: string;
}

export interface EmploymentForm {
  companyName: string;
  positionAndDuties: string;
  dateCommenced: string;
  dateEnded: string;
  isStillWorking: boolean;
}

export const applicationDocumentTypes = [
  "PASSPORT",
  "CURRENT_VISA",
  "MARRIAGE_CERTIFICATE",
  "ENGLISH_PROFICIENCY_CERTIFICATE",
  "HIGH_SCHOOL_CERTIFICATE",
  "HIGH_SCHOOL_TRANSCRIPT",
  "TERTIARY_CERTIFICATE_1",
  "TERTIARY_CERTIFICATE_2",
  "TERTIARY_CERTIFICATE_3",
  "TERTIARY_TRANSCRIPT_1",
  "TERTIARY_TRANSCRIPT_2",
  "TERTIARY_TRANSCRIPT_3",
  "CREDIT_TRANSFER",
  "BANK_STATEMENTS",
  "SCHOLARSHIP_DOCUMENTS",
  "SPONSOR_IDENTITY",
  "RELATIONSHIP_EVIDENCE",
  "PROPERTY_DEED",
  "BUSINESS_REGISTRATION_CERTIFICATE",
  "INCOME_TAX",
  "HEALTH_INSURANCE_EVIDENCE",
  "DEPENDENT_PASSPORT",
  "EMPLOYMENT_1_REFERENCE",
  "EMPLOYMENT_2_REFERENCE",
  "EMPLOYMENT_3_REFERENCE",
];

// export interface EmploymentDetailsForm {
//   currentEmploymentStatus: string;
//   reasonForUndertakingCourse: string[];
//   reasonOtherDetails: string;
// }
